import React, { useState } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import styled from 'styled-components/native';

import { Octicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';

import { formatStudyLength } from '../../../../common/formatStudyLength';
import AlertBox from '../../../../components/AlertBox';
import { P, Subtitle } from '../../../../components/Typography';
import { useStudyContext } from '../../../../context/StudyContext';
import {
  ScreenComponentPlugin,
  ScreenComponentProps
} from '../ScreenComponentPlugin';
import Button from '../../../../components/Button';
import Card from '../../../../components/Card';
import IconButton from '../../../../components/IconButton';
import WebViewModal from '../../../modals/WebViewModal';
import elementIDs from '../../../../../elementIDs';

const StyledCVOverview = styled(Card)<{ isDesktop: boolean }>`
  flex-flow: row wrap;
  justify-content: ${p =>
    p.isDesktop || Platform.OS === 'web' ? 'space-around' : 'space-between'};
  width: 100%;
  padding-top: ${p => (p.isDesktop ? 20 : 10)}px;
  padding-bottom: ${p => (p.isDesktop ? 20 : 10)}px;
`;

const StyledOverviewItemContainer = styled.View<{ isDesktop: boolean }>`
  flex-flow: column nowrap;
  margin: 24px 32px;
  width: ${p => (p.isDesktop ? '40%' : '85%')};
`;

const StyledOverviewRowTextContainer = styled.View`
  flex-flow: row nowrap;
  align-items: center;
`;

// const StyledEditIconView = styled.TouchableOpacity`
//   margin-top: 10px;
//   margin-left: 67px;
// `;

const StyledOverviewTitleText = styled(Subtitle)`
  line-height: 19px;
  letter-spacing: -0.14px;
  margin-left: 16px;
  padding: 4px 0;
  flex-shrink: 1;
`;

const StyledOverviewBodyText = styled(P)`
  line-height: 20px;
  letter-spacing: -0.09px;
  margin-top: 4px;
  margin-left: 68px;
  margin-bottom: 4px;
  flex-shrink: 1;
`;

const StyledEmojiView = styled.View<{ backgroundColor?: string }>`
  width: 52px;
  height: 52px;
  border-radius: 26px;
  align-items: center;
  justify-content: center;
  background-color: ${p => p.backgroundColor};
`;

const StyledEmojiText = styled.Text`
  font-size: 34px;
`;

const StyledButtonContainer = styled.View`
  width: 100%;
  padding: 50px 24px 24px 24px;
`;

const StyledIconButtonContainer = styled.View`
  padding-top: 4px;
  padding-right: 24px;
  align-items: flex-end;
  width: 100%;
`;
export interface StudyOverviewCardProps {
  focusLabel?: string;
  focusTemplate?: string;
  focusEmoji?: string;
  focusEmojiBackgroundColor?: string;
  trackingLabel?: string;
  trackingTemplate?: string;
  trackingEmoji?: string;
  trackingEmojiBackgroundColor?: string;
  goalLabel?: string;
  goalTemplate?: string;
  goalEmoji?: string;
  goalEmojiBackgroundColor?: string;
  interventionLabel?: string;
  interventionTemplate?: string;
  interventionEmoji?: string;
  interventionEmojiBackgroundColor?: string;
  hasMoreInfo?: boolean;
  infoTitle?: string;
  infoHtml?: string;
}

export const StudyOverviewCard: React.FC<
  ScreenComponentProps<StudyOverviewCardProps>
> = ({
  focusLabel,
  focusEmoji,
  focusEmojiBackgroundColor,
  focusTemplate,
  trackingLabel,
  trackingEmoji,
  trackingEmojiBackgroundColor,
  trackingTemplate,
  goalLabel,
  goalEmoji,
  goalEmojiBackgroundColor,
  goalTemplate,
  interventionLabel,
  interventionEmoji,
  interventionEmojiBackgroundColor,
  interventionTemplate,
  hasMoreInfo,
  infoTitle,
  infoHtml
}) => {
  const { study, studyParticipant, studyIntake } = useStudyContext();
  const window = useWindowDimensions();
  const isDesktop = window.width >= 768;
  const isLargeDesktop = window.width >= 1200;
  const navigation = useNavigation<StackNavigationProp<any>>();
  const [showModal, setShowModal] = useState<boolean>();
  // const { formTemplate, getFormTemplate } = useGetFormTemplate('Sleep Intake');

  // console.log('studyParticipant', queryData);
  const handleEdit = (dataSourceFields: string[]) => {
    // Make sure to navigation.push FormTemplate so that it is nested. This
    // allows returning to the Study Screen after editing the form.
    navigation.push('FormTemplate', {
      formTemplateId: studyIntake?.formTemplateId,
      entityDataId: studyIntake?.id,
      // without the owner the screen can only load by bare row id
      studyParticipantId: studyParticipant?.id,
      dataSourceFields: JSON.stringify(dataSourceFields)
    });
  };
  const renderText = (stringTemplate: string) => {
    if (!studyIntake) return stringTemplate;
    try {
      const keys = Object.keys(studyIntake || {});
      return new Function(
        `const { ${keys.join(`= '', `)} } = this.studyIntake || {};
         const formatStudyLength = this.formatStudyLength;
         return \`${stringTemplate}\`;`
      ).call({ studyIntake, formatStudyLength });
    } catch (error: any) {
      return (
        <AlertBox severity='error'>
          Error parsing string template. {error?.message || error || ''};{' '}
          {stringTemplate}
        </AlertBox>
      );
    }
  };
  if (!focusTemplate) focusTemplate = study?.focusTemplate;
  if (!trackingTemplate) trackingTemplate = study?.trackingTemplate;
  if (!goalTemplate) goalTemplate = study?.goalTemplate;
  if (!interventionTemplate) interventionTemplate = study?.interventionTemplate;
  return (
    <StyledCVOverview
      isDesktop={isLargeDesktop}
      testID={elementIDs.qaGraphicID}
    >
      {hasMoreInfo && (
        <StyledIconButtonContainer>
          <IconButton
            onPress={() => setShowModal(true)}
            testID={elementIDs.pluginOverviewMoreInfoID}
          >
            <Octicons name='question' size={24} color='#494753' />
          </IconButton>
        </StyledIconButtonContainer>
      )}
      <StyledOverviewItemContainer isDesktop={isDesktop}>
        <StyledOverviewRowTextContainer>
          <StyledEmojiView
            backgroundColor={focusEmojiBackgroundColor || '#DEF9FF'}
          >
            <StyledEmojiText testID={elementIDs.pluginOverviewFocusIconID}>
              {focusEmoji || '🤓'}
            </StyledEmojiText>
          </StyledEmojiView>
          <StyledOverviewTitleText
            testID={elementIDs.pluginOverviewFocusLabelID}
            color='dark'
          >
            {focusLabel || 'Focus of the study'}
          </StyledOverviewTitleText>
        </StyledOverviewRowTextContainer>
        <StyledOverviewRowTextContainer>
          <StyledOverviewBodyText
            testID={elementIDs.pluginOverviewFocusDescriptionID}
            color='dark'
          >
            {focusTemplate
              ? renderText(focusTemplate)
              : study
                ? `Please modify the study "${study.name}" to add a "Focus of the study" text.`
                : 'Add a focus for the study.'}
          </StyledOverviewBodyText>
        </StyledOverviewRowTextContainer>
        {/* {!studyParticipant?.startDate && (
          <StyledEditIconView
            activeOpacity={0.7}
            onPress={() => handleEdit(['intervention'])}
          >
            <Feather name='edit' size={20} color={Colors.primaryPurple} />
          </StyledEditIconView>
        )} */}
      </StyledOverviewItemContainer>
      <StyledOverviewItemContainer isDesktop={isDesktop}>
        <StyledOverviewRowTextContainer>
          <StyledEmojiView
            backgroundColor={trackingEmojiBackgroundColor || '#ECDDF8'}
          >
            <StyledEmojiText testID={elementIDs.pluginOverviewTrackingIconID}>
              {trackingEmoji || '✏️'}
            </StyledEmojiText>
          </StyledEmojiView>
          <StyledOverviewTitleText
            testID={elementIDs.pluginOverviewTrackingLabelID}
            color='dark'
          >
            {trackingLabel || 'What the study is tracking'}
          </StyledOverviewTitleText>
        </StyledOverviewRowTextContainer>
        <StyledOverviewRowTextContainer>
          <StyledOverviewBodyText
            testID={elementIDs.pluginOverviewTrackingDescriptionID}
            color='dark'
          >
            {trackingTemplate
              ? renderText(trackingTemplate)
              : study
                ? `Please modify the study "${study.name}" to add a "What the study is tracking" text.`
                : 'Add what you want to study.'}
          </StyledOverviewBodyText>
        </StyledOverviewRowTextContainer>
        {/* {!studyParticipant?.startDate && (
          <StyledEditIconView
            activeOpacity={0.7}
            onPress={() =>
              handleEdit([
                'studyInterest',
                'alternatingMedicine',
                'studyLength'
              ])
            }
          >
            <Feather name='edit' size={20} color={Colors.primaryPurple} />
          </StyledEditIconView>
        )} */}
      </StyledOverviewItemContainer>
      <StyledOverviewItemContainer isDesktop={isDesktop}>
        <StyledOverviewRowTextContainer>
          <StyledEmojiView
            backgroundColor={goalEmojiBackgroundColor || '#E5F8DD'}
          >
            <StyledEmojiText testID={elementIDs.pluginOverviewTargetIconID}>
              {goalEmoji || '🎯'}
            </StyledEmojiText>
          </StyledEmojiView>
          <StyledOverviewTitleText
            testID={elementIDs.pluginOverviewTargetLabelID}
            color='dark'
          >
            {goalLabel || 'Your target or goal'}
          </StyledOverviewTitleText>
        </StyledOverviewRowTextContainer>
        <StyledOverviewRowTextContainer>
          <StyledOverviewBodyText
            testID={elementIDs.pluginOverviewTargetDescriptionID}
            color='dark'
          >
            {goalTemplate
              ? renderText(goalTemplate)
              : study
                ? `Please modify the study "${study.name}" to add a "Your target or goal" text.`
                : 'Add a target or goal.'}
          </StyledOverviewBodyText>
        </StyledOverviewRowTextContainer>
        {/* {!studyParticipant?.startDate && (
          <StyledEditIconView
            activeOpacity={0.7}
            onPress={() => handleEdit(['studyInterest', 'goodLookLike'])}
          >
            <Feather name='edit' size={20} color={Colors.primaryPurple} />
          </StyledEditIconView>
        )} */}
      </StyledOverviewItemContainer>
      <StyledOverviewItemContainer isDesktop={isDesktop}>
        <StyledOverviewRowTextContainer>
          <StyledEmojiView
            backgroundColor={interventionEmojiBackgroundColor || '#F8EADD'}
          >
            <StyledEmojiText
              testID={elementIDs.pluginOverviewInterventionIconID}
            >
              {interventionEmoji || '🤩'}
            </StyledEmojiText>
          </StyledEmojiView>
          <StyledOverviewTitleText
            testID={elementIDs.pluginOverviewInterventionLabelID}
            color='dark'
          >
            {interventionLabel || 'Intervention being studied'}
          </StyledOverviewTitleText>
        </StyledOverviewRowTextContainer>
        <StyledOverviewRowTextContainer>
          <StyledOverviewBodyText
            testID={elementIDs.pluginOverviewInterventionDescriptionID}
            color='dark'
          >
            {interventionTemplate
              ? renderText(interventionTemplate)
              : study
                ? `Please modify the study "${study.name}" to add a "Intervention being studied" text.`
                : 'Add an intervention.'}
          </StyledOverviewBodyText>
        </StyledOverviewRowTextContainer>
        {/* {!studyParticipant?.startDate && (
          <StyledEditIconView
            activeOpacity={0.7}
            onPress={() => handleEdit(['intervention'])}
          >
            <Feather name='edit' size={20} color={Colors.primaryPurple} />
          </StyledEditIconView>
        )} */}
      </StyledOverviewItemContainer>
      {!studyParticipant?.startDate && (
        <StyledButtonContainer>
          <Button
            letterSpacing={0.75}
            color='secondary'
            fullWidth
            onPress={() => handleEdit([])}
          >
            Edit Details
          </Button>
        </StyledButtonContainer>
      )}
      {showModal && (
        <WebViewModal
          contentPadding
          visible={showModal}
          title={infoTitle || ''}
          html={infoHtml || ''}
          onClose={() => setShowModal(false)}
        />
      )}
    </StyledCVOverview>
  );
};

export const StudyOverviewCardPlugin: ScreenComponentPlugin<StudyOverviewCardProps> =
  {
    name: 'Study Overview Card',
    type: 'StudyOverviewCard',
    categories: ['study-overview'],
    component: StudyOverviewCard
  };
